import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { FileText, UploadSimple, ArrowSquareOut, Trash, CheckCircle } from "@phosphor-icons/react";

// The six canonical legal documents, in display order.
const CANONICAL = [
  "Privacy Policy",
  "Terms of Use",
  "Cookie Policy",
  "Security & Responsible AI Statement",
  "Grievance Redressal",
  "Contact & Corporate Information",
];

export default function PolicyDocsEditor({ settings, updateTheme, savingTheme, token }) {
  const [rows, setRows] = useState([]);
  const [uploadingName, setUploadingName] = useState(null);
  const fileInputs = useRef({});

  // Merge saved docs with the canonical list so all six slots always render.
  useEffect(() => {
    const saved = Array.isArray(settings?.policy_documents) ? settings.policy_documents : [];
    const byName = Object.fromEntries(saved.map((d) => [d.name, d]));
    setRows(CANONICAL.map((name) => byName[name] || { name, url: "", filename: "" }));
  }, [settings?.policy_documents]);

  const persist = (nextRows) => {
    // Save only slots that actually have a file uploaded.
    const cleaned = nextRows
      .filter((r) => r.url)
      .map((r) => ({ name: r.name, url: r.url, filename: r.filename || "", uploaded_at: r.uploaded_at || "" }));
    updateTheme({ policy_documents: cleaned });
  };

  const handleUpload = async (name, file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Only PDF, DOC, or DOCX files are allowed.");
      return;
    }
    setUploadingName(name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-doc`, fd, {
        headers: { "X-Admin-Token": token },
      });
      const next = rows.map((r) => (r.name === name ? { ...r, url: data.url, filename: file.name, uploaded_at: new Date().toISOString() } : r));
      setRows(next);
      persist(next);
      toast.success(`${name} uploaded.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed.";
      toast.error(msg);
    } finally {
      setUploadingName(null);
    }
  };

  const removeDoc = (name) => {
    const next = rows.map((r) => (r.name === name ? { ...r, url: "", filename: "" } : r));
    setRows(next);
    persist(next);
    toast.success(`${name} removed from the footer.`);
  };

  return (
    <div className="flex items-start gap-4" data-testid="policy-docs-editor">
      <FileText size={22} color="#00E5FF" weight="duotone" />
      <div className="flex-1">
        <h3 className="font-display text-xl">Legal / policy documents</h3>
        <p className="mt-1 text-[#8E8E93] text-sm">
          Upload the PDF or DOC for each policy. Uploaded documents appear as clickable
          links in the site footer — visitors can open or download them. Slots without a
          file simply won&apos;t show up until you upload one.
        </p>

        <div className="mt-5 divide-y divide-[#2A2A2A] border-y border-[#2A2A2A]">
          {rows.map((r) => (
            <div
              key={r.name}
              className="flex items-center gap-3 py-3 flex-wrap"
              data-testid={`policy-row-${r.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            >
              <div className="flex items-center gap-2 min-w-[240px] flex-1">
                {r.url ? (
                  <CheckCircle size={16} weight="fill" color="#00FFB2" />
                ) : (
                  <span className="w-4 h-4 rounded-full border border-[#2A2A2A] inline-block" />
                )}
                <div>
                  <div className="text-white text-sm">{r.name}</div>
                  {r.url ? (
                    <div className="font-mono text-[10px] text-[#8E8E93] truncate max-w-[280px]" title={r.filename}>
                      {r.filename || "uploaded"}
                      {r.uploaded_at ? ` · ${new Date(r.uploaded_at).toLocaleDateString()}` : ""}
                    </div>
                  ) : (
                    <div className="font-mono text-[10px] text-[#555]">No file uploaded</div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {r.url && (
                  <a
                    href={mediaUrl(r.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost text-[10px] py-2 px-2"
                    data-testid={`policy-open-${r.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  >
                    <ArrowSquareOut size={12} /> Open
                  </a>
                )}
                <label
                  className="btn-secondary text-[10px] py-2 px-3 cursor-pointer disabled:opacity-50"
                  data-testid={`policy-upload-${r.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  <UploadSimple size={12} />
                  {uploadingName === r.name ? "Uploading…" : r.url ? "Replace" : "Upload"}
                  <input
                    ref={(el) => (fileInputs.current[r.name] = el)}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    disabled={savingTheme || uploadingName === r.name}
                    onChange={(e) => {
                      handleUpload(r.name, e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
                {r.url && (
                  <button
                    onClick={() => removeDoc(r.name)}
                    className="text-[#8E8E93] hover:text-[#FF4500] p-2"
                    title="Remove from footer"
                    data-testid={`policy-remove-${r.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
